import { CloseOutlined } from '@mui/icons-material'
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../_helpers/hooks'
import { ITab } from '../../../_models/tabsInterface'
import { IUserData } from '../../../_models/userInterface'
import ProBadge from '../../../components/common/proBadge'
import ProfileDrawer from '../../../features/profileDrawer'
import { useUserPermissionAuthorization } from '../../../hooks'
import { pricingData, setPricingAlertModal } from '../../../slices/pricingSlice'
import { publicPagesState } from '../../../slices/publicPagesSlice'
import { headerTabs } from '../items'

interface IMainDrawerProps {
  window?: () => Window
  handleDrawerToggle: () => void
  mobileOpen: boolean
  userData: IUserData | Record<string, never>
  handleDrawerOnPopoverClick: () => void
  onDrawerClose?: () => void
  profileDrawerChildren?: {
    businessHeader?: React.ReactNode
    businessList?: React.ReactNode
    menuItems?: (onClose: () => void) => React.ReactNode
  }
}

const AdminDrawer = (props: IMainDrawerProps) => {
  const dispatch = useAppDispatch()
  const [isPermitted] = useUserPermissionAuthorization()
  const { t } = useTranslation()
  const { pricingLimits } = useAppSelector(pricingData)
  const { isSubscriptionBased } = useAppSelector(publicPagesState)
  const {
    window,
    handleDrawerToggle,
    mobileOpen,
    handleDrawerOnPopoverClick,
    userData,
    onDrawerClose,
    profileDrawerChildren,
  } = props
  const navigate = useNavigate()
  const container = window !== undefined ? () => window().document.body : undefined
  const drawerWidth = '100vw'
  const popoverPlacement = 'center'

  const handleClickOnListItem = (item: ITab) => {
    handleDrawerToggle()
    // Booking management and Scanner features are not available for Basic plan
    if (
      item.id === 2 &&
      isSubscriptionBased &&
      pricingLimits?.plan_limits?.booking_management?.has_permission === false
    )
      dispatch(
        setPricingAlertModal({
          status: true,
          content: t('adminLayout.pleaseUpgradeSubscriptionForFeature'),
        }),
      )
    else if (
      item.id === 3 &&
      isSubscriptionBased &&
      pricingLimits?.plan_limits?.scanner?.has_permission === false
    )
      dispatch(
        setPricingAlertModal({
          status: true,
          content: t('adminLayout.pleaseUpgradeSubscriptionForFeature'),
        }),
      )
    else navigate(item.path)
  }
  const filteredHeaderTabs = headerTabs.filter((tabItem) => {
    // If the menu item has permission requirement and permission is not satisfied, filter it out
    return !tabItem.permissions || isPermitted(tabItem.permissions) || userData?.is_business_admin
  })

  const showProBadge = (item: ITab) => {
    if (isSubscriptionBased && pricingLimits?.plan_limits) {
      if (item?.id === 2 && !pricingLimits?.plan_limits?.booking_management?.has_permission)
        return true
      else if (item?.id === 3 && !pricingLimits?.plan_limits?.scanner?.has_permission) {
        return true
      }
    }
    return false
  }
  // Drawer - mobile view
  const drawer = (
    <>
      <Box onClick={handleDrawerToggle} sx={{ textAlign: 'end' }}>
        <Box>
          <IconButton
            color='primary'
            aria-label='open drawer'
            edge='end'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <CloseOutlined />
          </IconButton>
        </Box>
      </Box>
      <Box display='flex' alignItems='center' justifyContent='center' sx={{ my: 2 }}>
        <ProfileDrawer
          handleDrawerToggle={handleDrawerToggle}
          userData={props?.userData}
          popoverPlacement={popoverPlacement}
          handleDrawerOnPopoverClick={handleDrawerOnPopoverClick}
          superAdmin={true}
          onDrawerClose={onDrawerClose}
        >
          {profileDrawerChildren}
        </ProfileDrawer>
      </Box>
      <Box onClick={handleDrawerToggle} sx={{ textAlign: 'end' }}>
        <List>
          {filteredHeaderTabs.map((item: ITab, index: number) => (
            <ListItem key={index}>
              <ListItemButton
                sx={{ textAlign: 'center' }}
                onClick={() => handleClickOnListItem(item)}
              >
                <Box sx={{ textAlign: 'center', width: '100%' }}>
                  <Badge
                    invisible={!showProBadge(item)}
                    badgeContent={
                      <ProBadge positionStyles={{ transform: 'translate(50%, -10%)' }} />
                    }
                  >
                    <ListItemText primary={t(`superAdminLayout.${[item?.label]}`)} />
                  </Badge>
                </Box>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </>
  )
  return (
    <>
      <Drawer
        container={container}
        variant='temporary'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default AdminDrawer
